<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { Info } from 'lucide-vue-next'
import type { AudienceRule, ProductWithPublicId } from '@/types/types'
import axios from 'axios'
import DateComparison from '../components/DateComparison.vue'
import AudienceDisclosure from '@/views/audience/components/AudienceDisclosure.vue'

import type { HighlightInfoFn } from '../composables/rulesMatcher'

const CONSUMED_PRODUCTS_AVAILABLE_FROM = new Date('2025-10-07T00:00:00+02:00')

// Ces clients ont des produits consommés dans OpenSearch antérieurs à octobre 2025.
const MERCHANT_IDS_WITH_CONSUMED_PRODUCTS_BEFORE_OCTOBER_2025 = new Set([
  338, 844, 1022, 1233, 3556, 6565, 6834, 7221, 7331, 7366
])

const { t } = useI18n()
const { user } = useLocalStorage()
const { relativeOperators, isAudienceRuleWithParameters } = useAudience()

const showsConsumedProductsAvailabilityNotice = computed(() => {
  const createdAt = user.value.merchant.createdAt
  if (!createdAt || MERCHANT_IDS_WITH_CONSUMED_PRODUCTS_BEFORE_OCTOBER_2025.has(user.value.merchant.id)) {
    return false
  }

  const createdAtDate = new Date(createdAt)
  if (Number.isNaN(createdAtDate.getTime())) {
    return false
  }

  return createdAtDate < CONSUMED_PRODUCTS_AVAILABLE_FROM
})

const props = withDefaults(
  defineProps<{
    rules?: Record<string, AudienceRule>
    classes: any
    disabled: boolean
    getHighlightInfo: HighlightInfoFn
  }>(),
  {
    rules: () => ({})
  }
)
const emit = defineEmits(['update:consumedProducts'])
const rules = useVModel(props, 'rules')

if (!rules.value.consumedProduct) {
  rules.value.consumedProduct = { value: [], operator: 'in' }
}

const selectedProducts = ref<ProductWithPublicId[]>([])
const allProducts = ref<ProductWithPublicId[]>([])
const productsLoaded = ref(false)

watch(
  selectedProducts,
  (newProducts) => {
    emit('update:consumedProducts', newProducts)
  },
  { deep: true }
)
const isFetchingProducts = ref(false)

const fetchSelectedProducts = async (publicIds: string[]) => {
  if (!publicIds || publicIds.length === 0) {
    selectedProducts.value = []
    return
  }
  try {
    const { data } = await axios.get('/products', {
      params: {
        includeMenus: 1,
        onlyProductConsumptionPartners: 1,
        light: 1,
        paginated: 0,
        publicIds: publicIds
      }
    })
    selectedProducts.value = data.data || []
  } catch (error) {
    console.error('Error fetching selected products:', error)
    selectedProducts.value = []
  }
}

const fetchAllProducts = async (search: string = '') => {
  if (productsLoaded.value || isFetchingProducts.value) return
  isFetchingProducts.value = true
  try {
    const response = await axios.get('/products', {
      params: {
        search: search ?? null,
        includeMenus: 1,
        onlyProductConsumptionPartners: 1,
        light: 1,
        paginated: 0
      }
    })
    allProducts.value = response.data.data || []
    productsLoaded.value = true
  } catch (error) {
    console.error('Error fetching all products:', error)
    allProducts.value = []
  } finally {
    isFetchingProducts.value = false
  }
}

const onOpenProducts = async () => {
  await fetchAllProducts()
}

const selectedProductPublicIds = computed<string[]>({
  get: () => {
    if (!rules.value.consumedProduct) {
      return []
    }
    const value = rules.value.consumedProduct.value
    if (!Array.isArray(value)) return []
    return value.map((id) => String(id))
  },
  set: (ids) => {
    if (!rules.value.consumedProduct) {
      rules.value.consumedProduct = { value: [], operator: 'in' }
    }
    rules.value.consumedProduct.value = ids
  }
})

const OPERATOR_IN = 1
const OPERATOR_NOT_IN = 2

const OPERATOR_LTE = 1
const OPERATOR_GTE = 2
const OPERATOR_EQ = 3

const selectedOperatorId = computed<number>({
  get: () => (rules.value.consumedProduct?.operator === 'ni' ? OPERATOR_NOT_IN : OPERATOR_IN),
  set: (id: number) => {
    if (!rules.value.consumedProduct) return
    rules.value.consumedProduct.operator = id === OPERATOR_NOT_IN ? 'ni' : 'in'
  }
})

const updateOperator = (id: number) => {
  selectedOperatorId.value = id
}

const operatorOptions = computed(() => [
  { id: OPERATOR_IN, text: t('pages.audience.operators.consumedProduct.in') },
  { id: OPERATOR_NOT_IN, text: t('pages.audience.operators.consumedProduct.ni') }
])

const productOptions = computed(() => {
  return allProducts.value.map((p) => ({
    value: p.publicId,
    label: p.name,
    externalId: p.externalId,
    disabled: false
  }))
})

const isLoading = computed(() => isFetchingProducts.value)

const setInitialSelection = () => {
  const current = (rules.value.consumedProduct?.value as string[]) || []
  selectedProductPublicIds.value = Array.isArray(current) ? current : []
}

const isAutoSelecting = ref(false)

const autoSelectSimilarProducts = (newIds: string[], oldIds: string[]) => {
  if (!productsLoaded.value || allProducts.value.length === 0) {
    return newIds
  }

  const addedIds = newIds.filter((id) => !oldIds.includes(id))

  if (addedIds.length === 0) {
    return newIds
  }

  const productsToAdd = new Set<string>(newIds)

  addedIds.forEach((addedId) => {
    const addedProduct = allProducts.value.find((p) => p.publicId === addedId)
    if (addedProduct) {
      const sameNameProducts = allProducts.value.filter(
        (p) => p.name === addedProduct.name && !productsToAdd.has(p.publicId)
      )
      sameNameProducts.forEach((p) => productsToAdd.add(p.publicId))
    }
  })

  return Array.from(productsToAdd)
}

watch(selectedProductPublicIds, async (newIds, oldIds) => {
  if (isAutoSelecting.value) {
    isAutoSelecting.value = false
    return
  }

  const finalIds = autoSelectSimilarProducts(newIds, oldIds)

  if (finalIds.length > newIds.length) {
    isAutoSelecting.value = true
    selectedProductPublicIds.value = finalIds
    return
  }

  await fetchSelectedProducts(selectedProductPublicIds.value)
})

const displayDateBlock = ref(false)
const showQuantity = ref(false)

const ensureParameters = () => {
  if (!isAudienceRuleWithParameters(rules.value.consumedProduct)) {
    rules.value.consumedProduct = {
      ...rules.value.consumedProduct,
      parameters: {
        date_filter: {
          value: [],
          operator: '',
          type: ''
        }
      }
    }
  }
}

const openOptionalCondition = (type: 'date' | 'quantity') => {
  ensureParameters()
  if (
    type === 'quantity' &&
    (rules.value.consumedProduct.parameters.quantity_filter == null ||
      rules.value.consumedProduct.parameters.quantity_filter.value == null)
  ) {
    rules.value.consumedProduct.parameters.quantity_filter = {
      operator: 'gte',
      value: 1
    }
  }
  if (type === 'date') displayDateBlock.value = true
  if (type === 'quantity') showQuantity.value = true
}

const resetQuantityCondition = () => {
  if (rules.value.consumedProduct?.parameters) {
    delete rules.value.consumedProduct.parameters.quantity_filter
  }
  showQuantity.value = false
}

const canAddDate = computed(() => !props.disabled && !displayDateBlock.value)
const canAddQuantity = computed(() => !props.disabled && !showQuantity.value)

const resetDateCondition = () => {
  if (!rules.value.consumedProduct?.parameters) return
  rules.value.consumedProduct.parameters = {
    ...rules.value.consumedProduct.parameters,
    date_filter: {
      value: [],
      type: '',
      operator: ''
    }
  }
  displayDateBlock.value = false
}

const displayQuantityBlock = computed(() => showQuantity.value)

const capitalize = (str: string): string => {
  if (!str) {
    return ''
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const quantityOperatorOptions = computed(() => {
  return [
    { id: OPERATOR_EQ, text: capitalize(t('pages.audience.operators.quantity.eq')), operator: 'eq' },
    { id: OPERATOR_GTE, text: capitalize(t('pages.audience.operators.quantity.atLeast')), operator: 'gte' },
    { id: OPERATOR_LTE, text: capitalize(t('pages.audience.operators.quantity.atMost')), operator: 'lte' }
  ]
})

const selectedQuantityOperatorId = computed<number>({
  get: () =>
    quantityOperatorOptions.value.find(
      (op) => op.operator === rules.value.consumedProduct?.parameters?.quantity_filter?.operator
    )?.id ?? OPERATOR_EQ,
  set: (id: number) => {
    if (!rules.value.consumedProduct) return
    rules.value.consumedProduct.parameters.quantity_filter.operator = quantityOperatorOptions.value.find(
      (op) => op.id === id
    )?.operator as string
  }
})

const updateQuantityOperator = (id: number) => {
  selectedQuantityOperatorId.value = id
}

const quantityValue = computed({
  get: () => {
    const raw = rules.value.consumedProduct?.parameters?.quantity_filter?.value
    return raw == null || raw === '' ? '' : String(raw)
  },
  set: (val: string) => {
    if (!rules.value.consumedProduct?.parameters?.quantity_filter) return
    rules.value.consumedProduct.parameters.quantity_filter.value = val === '' ? '' : Number(val)
  }
})

const consumedProductHighlight = computed(() => props.getHighlightInfo('consumedProduct'))
const activeFiltersCount = computed(() => {
  let count = 0
  if (Array.isArray(rules.value.consumedProduct?.value) && rules.value.consumedProduct.value.length > 0) count++
  return count
})

onMounted(async () => {
  if (Array.isArray(rules.value.consumedProduct?.value) && rules.value.consumedProduct.value.length > 0) {
    await fetchAllProducts()
  }

  setInitialSelection()
  await fetchSelectedProducts(selectedProductPublicIds.value)

  if (isAudienceRuleWithParameters(rules.value.consumedProduct)) {
    const hasDate =
      Array.isArray(rules.value.consumedProduct.parameters?.date_filter?.value) &&
      rules.value.consumedProduct.parameters.date_filter.value.length > 0
    const hasQuantity = typeof rules.value.consumedProduct.parameters?.quantity_filter?.value === 'number'
    displayDateBlock.value = !!hasDate
    showQuantity.value = !!hasQuantity
  }
})
</script>

<template>
  <AudienceDisclosure
    :title="t('pages.audience.audienceCreatePage.block.consumedProduct.title')"
    :subtitle="t('pages.audience.audienceCreatePage.block.consumedProduct.description')"
    icon="HeroShoppingBagIconOutline"
    :active-filters-count="activeFiltersCount"
  >
    <Alert v-if="showsConsumedProductsAvailabilityNotice">
      <Info />
      <AlertDescription>
        {{ t('pages.audience.audienceCreatePage.block.consumedProduct.availabilityNotice') }}
      </AlertDescription>
    </Alert>
    <div
      :class="[
        classes.blockContainer,
        'flex flex-col gap-5',
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': consumedProductHighlight.hasDefined
        }
      ]"
    >
      <div :class="classes.labelContainer">
        <label :class="classes.label">
          {{ t('pages.audience.audienceCreatePage.block.consumedProduct.product.label') }}
        </label>
      </div>
      <div class="flex flex-col gap-6">
        <div class="flex w-full items-center gap-5 mb-2">
          <div class="w-56">
            <ComboboxSelect
              value-key="id"
              label-key="text"
              trigger-class="w-full"
              :disabled="disabled"
              :placeholder="''"
              :model-value="selectedOperatorId"
              :options="operatorOptions"
              @update:model-value="updateOperator"
            />
          </div>
          <BaseMultiSelect
            v-model="selectedProductPublicIds"
            class="flex-1"
            :disabled="disabled"
            :loading="isLoading"
            :options="productOptions"
            :fluid="true"
            :filter="true"
            :filter-match-mode="'contains'"
            :filter-placeholder="t('global.multiselect.products.searchPlaceholder')"
            :empty-filter-message="t('global.multiselect.products.noResultSearch')"
            :show-toggle-all="false"
            :aria-label="t('pages.audience.audienceCreatePage.block.consumedProduct.product.label')"
            :placeholder="t('global.multiselect.products.placeholder')"
            @show="onOpenProducts"
          >
            <template #option="{ option }">
              <div class="flex items-center justify-between w-full">
                <div class="font-regular text-foreground truncate">{{ option.label }}</div>
                <span
                  v-if="option.externalId"
                  class="ml-3 shrink-0 tabular-nums text-xs text-muted-foreground text-right"
                  >ID: {{ option.externalId }}</span
                >
              </div>
            </template>
          </BaseMultiSelect>
        </div>
      </div>
      <div v-if="canAddDate" class="grid grid-cols-10 gap-2">
        <div class="col-span-9">
          <TreeNode>
            <template #content>
              <div class="tree-content flex flex-col items-start gap-4">
                <Button type="button" variant="ghost" :disabled="disabled" @click="() => openOptionalCondition('date')">
                  <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5" />
                  {{ t('pages.audience.operators.date.conditionBtn') }}
                </Button>
              </div>
            </template>
          </TreeNode>
        </div>
      </div>
      <div v-if="displayDateBlock" class="grid grid-cols-10">
        <TreeNode class="col-span-9" content-class="w-full">
          <template #content>
            <DateComparison
              is-relative-rule
              :operators="relativeOperators"
              :rule="rules.consumedProduct"
              :disabled="disabled"
            />
          </template>
        </TreeNode>
        <Button
          v-if="displayDateBlock && !disabled"
          variant="ghost"
          size="icon"
          class="col-start-10 aspect-square w-fit justify-self-end text-foreground-secondary hover:text-red-600"
          @click="resetDateCondition"
        >
          <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5" />
        </Button>
      </div>
      <div v-if="displayQuantityBlock" class="grid grid-cols-10">
        <TreeNode class="col-span-9" content-class="w-full">
          <template #content>
            <div class="flex flex-col items-center space-y-5 sm:grid sm:grid-cols-5 sm:gap-5 md:space-y-0">
              <div class="sm:col-span-3">
                <ComboboxSelect
                  value-key="id"
                  label-key="text"
                  trigger-class="w-full"
                  :disabled="disabled"
                  :placeholder="''"
                  :model-value="selectedQuantityOperatorId"
                  :options="quantityOperatorOptions"
                  @update:model-value="updateQuantityOperator"
                />
              </div>
              <AppFormInput
                id="consumed-qty"
                v-model="quantityValue"
                class="sm:col-span-2 mx-1"
                type="number"
                :disabled="disabled"
                :min="1"
              >
                <template #icon-right>
                  <span class="text-sm text-muted-foreground">{{ t('global.words.times') }}</span>
                </template>
              </AppFormInput>
            </div>
          </template>
        </TreeNode>
        <Button
          v-if="displayQuantityBlock && !disabled"
          variant="ghost"
          size="icon"
          class="col-start-10 aspect-square w-fit justify-self-end cursor-pointer text-foreground-secondary hover:text-red-600"
          @click="resetQuantityCondition"
        >
          <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5" />
        </Button>
      </div>
      <div v-if="canAddQuantity" class="grid grid-cols-10 gap-2">
        <TreeNode class="col-span-9">
          <template #content>
            <div class="tree-content flex flex-col items-start gap-4">
              <Button
                type="button"
                variant="ghost"
                :disabled="disabled"
                @click="() => openOptionalCondition('quantity')"
              >
                <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5" />
                {{ t('pages.audience.operators.quantity.conditionBtn') }}
              </Button>
            </div>
          </template>
        </TreeNode>
      </div>
    </div>
  </AudienceDisclosure>
</template>
